import React, { useState, useRef, useEffect } from "react";
import {
    Box,
    IconButton,
    TextField,
    Button,
    Paper,
    Typography,
    List,
    ListItem,
    ListItemButton,
    ListItemText,
} from "@mui/material";
import ChatIcon from "@mui/icons-material/Chat";
import CloseIcon from "@mui/icons-material/Close";
import SendIcon from "@mui/icons-material/Send";
import { MyTextField } from "../StyledComponents";
import { useTheme } from "@emotion/react";

const predefinedQuestions = {
    "What technologies do you specialize in?": "I specialize in MERN stack (MongoDB, Express, React, Node.js), Java, SQL, and UI/UX design with MUI.",
    "Do you provide training sessions?": "Yes! I offer training in web development, JavaScript, React, and backend technologies.",
    "Can you build full-stack applications?": "Absolutely! I develop full-stack applications using MERN stack, integrating databases, APIs, and modern UI frameworks.",
    "Do you offer mentoring or workshops?": "Yes, I conduct workshops and mentorship programs on web development, React, and database management.",
    "Can you help with performance optimization?": "Yes! I optimize web applications by improving database queries, reducing bundle sizes, and enhancing rendering performance.",
    "What databases do you work with?": "I primarily work with MongoDB and SQL databases, designing scalable and efficient data structures.",
    "Can you integrate third-party APIs?": "Yes, I have experience integrating various third-party APIs, including payment gateways, authentication services, and cloud solutions.",
    "Do you build RESTful and GraphQL APIs?": "Yes, I design and develop RESTful APIs and GraphQL services to ensure smooth data communication.",
    "How can I stay updated with your latest projects?": "You can follow my LinkedIn profile or visit my portfolio to check out my latest work.",
    "How can I contact you for a project or training?": "You can reach me via email, LinkedIn, or through the contact section on my website."
};

const contactInfo = (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
        <Typography>
            I'm sorry, I don't have an answer for that. You can contact me using these options
        </Typography>
        <Typography>
            📞{" "}
            <a href="tel:+917989738066" style={{ textDecoration: "none", color: "#1976d2", fontWeight: "bold" }}>
                +91 7989738066
            </a>
        </Typography>
        <Typography>
            📧{" "}
            <a href="mailto:kadapasharief786@gmail.com" style={{ textDecoration: "none", color: "#1976d2", fontWeight: "bold" }}>
                kadapasharief786@gmail.com
            </a>
        </Typography>
        <Typography>
            🔗{" "}
            <a href="https://www.linkedin.com/in/sharief-k-89a046169/" target="_blank" rel="noopener noreferrer"
                style={{ textDecoration: "none", color: "#1976d2", fontWeight: "bold" }}>
                LinkedIn Profile
            </a>
        </Typography>
    </Box>
);

const ChatBot = () => {
    const [open, setOpen] = useState(false);
    const [messages, setMessages] = useState([{ text: "Hi! How can I help you?", sender: "bot" }]);
    const [input, setInput] = useState("");
    const [position, setPosition] = useState({ top: 500, left: 20 });
    const chatEndRef = useRef(null);
    const chatBoxRef = useRef(null);
    const isDragging = useRef(false);
    const dragOffset = useRef({ x: 0, y: 0 });
    let theme=useTheme();

    useEffect(() => {
        chatEndRef.current?.scrollIntoView({ behavior: "smooth" });
    }, [messages]);
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (chatBoxRef.current && !chatBoxRef.current.contains(event.target)) {
                setOpen(false);
            }
        };

        if (open) {
            document.addEventListener("mousedown", handleClickOutside);
        } else {
            document.removeEventListener("mousedown", handleClickOutside);
        }

        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, [open]);

    const toggleChat = () => setOpen(!open);

    const sendMessage = (text) => {
        if (!text.trim()) return;
        setMessages((prev) => [...prev, { text, sender: "user" }]);

        setTimeout(() => {
            const reply = predefinedQuestions[text] || contactInfo;
            setMessages((prev) => [...prev, { text: reply, sender: "bot" }]);
        }, 800);
    };

    const handleSend = () => {
        sendMessage(input);
        setInput("");
    };

    const startDrag = (e) => {
        e.preventDefault();
        isDragging.current = true;

        let startX = e.clientX ?? e.touches?.[0]?.clientX ?? 0;
        let startY = e.clientY ?? e.touches?.[0]?.clientY ?? 0;

        dragOffset.current = {
            x: startX - position.left,
            y: startY - position.top,
        };

        document.body.style.overflow = "hidden"; // Disable background scroll

        const onMove = (moveEvent) => {
            if (!isDragging.current) return;
            moveEvent.preventDefault();

            let moveX = moveEvent.clientX ?? moveEvent.touches?.[0]?.clientX ?? 0;
            let moveY = moveEvent.clientY ?? moveEvent.touches?.[0]?.clientY ?? 0;

            requestAnimationFrame(() => {
                setPosition((prev) => ({
                    top: Math.max(0, Math.min(window.innerHeight - 100, moveY - dragOffset.current.y)), // Keep inside screen
                    left: Math.max(0, Math.min(window.innerWidth - 60, moveX - dragOffset.current.x)), // Keep inside screen
                }));
            });
        };

        const stopDrag = () => {
            isDragging.current = false;
            document.body.style.overflow = "auto"; // Re-enable scrolling
            document.removeEventListener("mousemove", onMove);
            document.removeEventListener("mouseup", stopDrag);
            document.removeEventListener("touchmove", onMove);
            document.removeEventListener("touchend", stopDrag);
        };

        document.addEventListener("mousemove", onMove);
        document.addEventListener("mouseup", stopDrag);
        document.addEventListener("touchmove", onMove, { passive: false });
        document.addEventListener("touchend", stopDrag);
    };

    return (
        <>
            {/* ✅ Draggable Chat Icon & Chat Window */}
            <Box
                sx={{
                    position: "fixed",
                    top: position.top,
                    left: position.left,
                    zIndex: 1000,
                    userSelect: "none",
                }}
            >
                {/* Chat Button */}
                <IconButton
                    onClick={toggleChat}
                    onMouseDown={startDrag}
                    onTouchStart={startDrag}
                    sx={{
                        backgroundColor: "secondary.main",
                        color: "text.primary",
                        "&:hover": { backgroundColor: "secondary.dark" },
                        boxShadow: 3,
                        animation: !open ? "jump 1.5s infinite" : "none", // Apply animation when closed
                        "@keyframes jump": {
                            "0%, 100%": { transform: "translateY(0)" },
                            "50%": { transform: "translateY(-10px)" }, // Moves up slightly
                        },
                    }}
                >
                    {open ? <CloseIcon /> : <ChatIcon />}
                </IconButton>

                {/* Chat Window */}
                {open && (
                    <Paper
                        ref={chatBoxRef}
                        sx={{
                            width: 350,
                            height: 450,
                            display: "flex",
                            flexDirection: "column",
                            borderRadius: 3,
                            boxShadow: 5,
                            overflow: "hidden",
                            backgroundColor: "background.default",
                            border:`1px solid ${theme.palette.shade.light}`,
                            marginTop: 2,
                            userSelect: "none",
                            position: "absolute",
                            transform: position.left > window.innerWidth - 250 ? "translateX(-90%)" : "none",
                        }}
                    >
                        {/* Chat Header */}
                        <Box
                            sx={{
                                backgroundColor: "secondary.main",
                                color: "white",
                                p: 2,
                                textAlign: "center",
                                cursor: "grab",
                            }}
                            onMouseDown={startDrag}
                            onTouchStart={startDrag}
                        >
                            <Typography variant="h6">ChatBot</Typography>
                        </Box>

                        {/* Messages */}
                        <Box
                            sx={{
                                flex: 1,
                                p: 2,
                                overflowY: "auto",
                                display: "flex",
                                flexDirection: "column",
                                gap: 1,
                                maxHeight: 320,
                            }}
                        >
                            {messages.map((msg, index) => (
                                <Typography
                                    key={index}
                                    sx={{
                                        alignSelf: msg.sender === "user" ? "flex-end" : "flex-start",
                                        backgroundColor: msg.sender === "user" ? "secondary.main" : "#f0f0f0",
                                        color: msg.sender === "user" ? "white" : "black",
                                        p: 1,
                                        borderRadius: 2,
                                        maxWidth: "80%",
                                        whiteSpace: "pre-line",
                                    }}
                                >
                                    {msg.text}
                                </Typography>
                            ))}
                            <div ref={chatEndRef} />
                        </Box>

                        {/* Suggested Questions */}
                        <List dense sx={{ borderTop: "1px solid #ddd", maxHeight: "120px", overflowY: "auto" }}>
                            {Object.keys(predefinedQuestions).map((question, index) => (
                                <ListItem key={index} disablePadding sx={{ "&:hover": { backgroundColor: "shade.light" } }}>
                                    <ListItemButton onClick={() => sendMessage(question)}>
                                        <ListItemText primary={question} />
                                    </ListItemButton>
                                </ListItem>
                            ))}
                        </List>


                        {/* Input Field */}
                        <Box sx={{ display: "flex", p: 1, borderTop: "1px solid #ddd" }}>
                            <MyTextField
                                fullWidth
                                size="small"
                                value={input}
                                onChange={(e) => setInput(e.target.value)}
                                placeholder="Type a message..."
                            />
                            <Button onClick={handleSend} sx={{ ml: 1 }} variant="contained">
                                <SendIcon />
                            </Button>
                        </Box>
                    </Paper>
                )}
            </Box>
        </>
    );
};

export default ChatBot;
